import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";

import { Button } from "@/components/ui/button";
import ButtonContainer from "@/components/button-container";
import CredentialsHeader from "@/components/header/Header";
import NeedHelpButton from "@/components/need-help-button/need-help-button";
import SimpleErrorMessage from "@/components/error-message/new-error-message";

import { DigitalAssetContext } from "@/contexts/continente-credentials/DigitalAssetContext";

import { useApiResponseHandler } from "@/hooks/useApiResponseHandler";
import { FormError } from "@/models/continente-credentials/common/FormError";
import { FreshStartResponse } from "@/models/shared/freshStart/FreshStartModels";

import { tryCatch } from "@/utils/try-catch";
import { genericService } from "@/services/GenericService";

type Form = FormError;

export default function FreshStartSuccess() {
    const { t } = useTranslation();
    const history = useHistory();
    const location = useLocation<any>();
    const digitalAssetContext = useContext(DigitalAssetContext);
    const { handleResponse } = useApiResponseHandler();

    const [loading, setLoading] = useState(false);

    const nextStep = location.state?.nextStep || "";

    const formState = useForm<Form>({
        mode: "onSubmit",
        reValidateMode: "onSubmit",
    });

    const {
        setError,
        clearErrors,
        formState: { errors },
    } = formState;

    const createAccount = async () => {
        clearErrors();
        setLoading(true);

        const { data: response, error } = await tryCatch(genericService.post<FreshStartResponse>(nextStep));

        handleResponse(response, { setError }, error);
        setLoading(false);
    };

    const goToStart = () => history.push(`/auth/${digitalAssetContext.digitalAsset.clientId}`);

    return (
        <div className="flex flex-col w-full items-center gap-6">
            <CredentialsHeader
                title={t("phonenumber_login_fresh_start_success.header_title")}
                subtitle={t("phonenumber_login_fresh_start_success.header_message")}
                showLoading={loading}
                showBackButton={false}
            />

            {errors.submitError?.message && (
                <SimpleErrorMessage
                    id="fresh_start_success__error-message"
                    error={errors.submitError.message}
                />
            )}

            <ButtonContainer>
                <Button
                    type="button"
                    disabled={loading}
                    onClick={createAccount}
                    aria-label={t("accessibility.phonenumber.login.fresh_start_success.continue")}
                >
                    {t("phonenumber_login_fresh_start_success.continue_button")}
                </Button>
                <Button
                    type="button"
                    variant={"secondary"}
                    disabled={loading}
                    onClick={goToStart}
                    aria-label={t("accessibility.phonenumber.login.fresh_start_success.back_to_start")}
                >
                    {t("phonenumber_login_fresh_start_success.back_to_start_button")}
                </Button>
                <NeedHelpButton />
            </ButtonContainer>
        </div>
    );
}
